import { useState } from "react";
import { toast } from "sonner";
import axios, { API } from "@/lib/api";
import { formatApiError } from "@/lib/apiError";
import { X, Mail, Loader2 } from "lucide-react";

export default function InviteDialog({ workspaceId, onClose, onSent }) {
  const [email, setEmail] = useState("");
  const [creditsInit, setCreditsInit] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/workspaces/${workspaceId}/invite`, {
        email: email.trim().toLowerCase(),
        initial_credits: Number(creditsInit) || 0,
      });
      toast.success(`Invite emailed to ${email}.`);
      onSent();
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't send invite."));
    } finally {
      setSending(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="team-invite-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl bg-[#0E0E0E] border border-white/10 p-7"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full border border-white/10 text-white/55 hover:text-white flex items-center justify-center"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <h2 className="font-display text-2xl text-white">Invite a designer</h2>
        <p className="text-sm text-white/55 mt-1">
          They&apos;ll get an email with a magic link to claim their seat.
          You can pre-load credits to their wallet now (debited from yours)
          or push later from the team table.
        </p>
        <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mt-5 mb-1.5">
          Designer email
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="anjali@khannajewellers.com"
          data-testid="team-invite-email"
          className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
        />
        <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mt-4 mb-1.5">
          Pre-allocate credits (optional)
        </label>
        <input
          type="number"
          min={0}
          max={10000}
          step={1}
          value={creditsInit}
          onChange={(e) => setCreditsInit(e.target.value)}
          placeholder="0"
          data-testid="team-invite-credits"
          className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
        />
        <button
          onClick={submit}
          disabled={sending || !email.includes("@")}
          data-testid="team-invite-submit"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-2.5 hover:bg-[#F3CE5A] transition disabled:opacity-60"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          {sending ? "Sending…" : "Send invite"}
        </button>
      </div>
    </div>
  );
}
